import * as angular from 'angular';

import { IRequestService } from '../request.service';
import { Dictionary, DigitalSet, IDisplayedColumn, IMappingDictionary, ImportResultDto, IValueViewModel, ObjectUser } from '../../models';
import { IExportToCsvService, IUploadService } from '../../services';

export interface IDigitalSetService {
    addOrUpdate(value: DigitalSet): angular.IPromise<DigitalSet>;
    deleteDigitalSet(guid: string): angular.IPromise<void>;
    download(set: DigitalSet): void;
    getAll(): angular.IPromise<DigitalSet[]>;
    getAll(force: boolean): angular.IPromise<DigitalSet[]>;
    getUsers(force?: boolean): angular.IPromise<Dictionary<ObjectUser[]>>;
    refresh(): angular.IPromise<DigitalSet[]>;
    upload(file: File, guid: string): angular.IPromise<ImportResultDto<DigitalSet>>;
}

export class DigitalSetService implements IDigitalSetService {
    static serviceId = 'digitalSetService';

    private _controllerUrl = 'digitalSet';
    private _propertyMapper: IMappingDictionary<IValueViewModel & { linked?: IValueViewModel }>;
    private _toExport: IDisplayedColumn[] = [];

    static $inject = [
        'exportToCsvService',
        'requestService',
        'uploadService'
    ];

    constructor(
        private _exportToCsv: IExportToCsvService,
        private _requestService: IRequestService,
        private _upload: IUploadService
    ) {
        this._toExport = [
            { isVisible: true, property: 'code', type: 'value', label: 'Code' },
            { isVisible: true, property: 'name', type: 'name', label: 'Name' }
        ];
        this.initiazePropertyMapper(this._toExport);
    }

    private controllerUrl() {
        return `${this._requestService.getUrlV1()}${this._controllerUrl}/`;
    }

    addOrUpdate(value: DigitalSet): angular.IPromise<DigitalSet> {
        return this._requestService
            .postAtV1<DigitalSet>(this._controllerUrl, value)
            .then(data => {
                this._requestService.cleanCacheAtV1(this._controllerUrl);
                return data;
            });
    }

    deleteDigitalSet(guid: string): angular.IPromise<void> {
        return this._requestService
            .postAtV1<void>(this._controllerUrl + '/delete/' + guid)
            .then(() => {
                this._requestService.cleanCacheAtV1(this._controllerUrl);
            });
    }

    download(set: DigitalSet): void {
        const data = this._exportToCsv.getCsvContent(this._toExport, set.stateCollection, this._propertyMapper, true);
        return this._exportToCsv.getCsvFromData(data, `digital-states-${set.name.toLocaleLowerCase()}.csv`);
    }

    getAll(): angular.IPromise<DigitalSet[]>;
    getAll(force: boolean): angular.IPromise<DigitalSet[]>;
    getAll(force?: boolean): angular.IPromise<DigitalSet[]> {
        if (force) {
            this._requestService.cleanCacheAtV1(this._controllerUrl);
        }
        return this._requestService.getAtV1(this._controllerUrl);
    }

    
    getUsers(force?: boolean): angular.IPromise<Dictionary<ObjectUser[]>> {
        const url = `${this._controllerUrl}/users`;
        if(force){
            this._requestService.cleanCacheAtV1(url);
        }
        return this._requestService.getAtV1(url);
    }

    refresh(): angular.IPromise<DigitalSet[]> {
        return this.getAll(true);
    }

    upload(file: File, guid: string): angular.IPromise<ImportResultDto<DigitalSet>> {
        const url = this.controllerUrl() + 'upload/?digitalSetGuid=' + guid;
        return this._upload
            .uploadFile<ImportResultDto<DigitalSet>>(url, file)
            .then(response => {
                this._requestService.cleanCacheAtV1(url);
                this._requestService.cleanCacheAtV1('digitalSet');
                return response;
            });
    }

    private initiazePropertyMapper(properties: IDisplayedColumn[]) {
        this._propertyMapper = {};
        if (properties) {
            properties.forEach(m => this._propertyMapper[m.property] = v => v[m.property]);
        }
    }
}
